import React from "react";
import { CheckCircle2, Loader2, Lock, Pencil, Save } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuickBooksTradeConfiguration } from "../../../hooks/useQuickBooksTradeConfiguration";
import ConfirmTradeSelectionModal from "../ConfirmTradeSelectionModal/ConfirmTradeSelectionModal";

interface QuickBooksTradeConfigurationProps {
  companyUuid: string;
  isTradeEditable: boolean;
}

const QuickBooksTradeConfiguration: React.FC<
  QuickBooksTradeConfigurationProps
> = ({ companyUuid, isTradeEditable }) => {
  const {
    cancelEditing,
    closeConfirm,
    confirmSave,
    error,
    isConfirmOpen,
    isEditing,
    isLoading,
    isSaving,
    quickBooksTrade,
    requestSave,
    selectedTradeId,
    selectedTradeName,
    setSelectedTradeId,
    startEditing,
    trades,
  } = useQuickBooksTradeConfiguration(companyUuid);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading trade configuration...
      </div>
    );
  }

  return (
    <>
      {!quickBooksTrade || isEditing ? (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Trade <span className="text-red-500">*</span>
              </label>
              <Select
                onValueChange={setSelectedTradeId}
                value={selectedTradeId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a trade..." />
                </SelectTrigger>
                <SelectContent>
                  {trades.map((trade) => (
                    <SelectItem key={trade.id} value={trade.id.toString()}>
                      {trade.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              className="flex h-9 flex-shrink-0 items-center justify-center gap-2 rounded-md border border-transparent bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!selectedTradeId}
              onClick={requestSave}
            >
              <Save className="h-4 w-4" />
              Save Trade
            </button>
            {isEditing && (
              <button
                className="h-9 flex-shrink-0 rounded-md border border-gray-200 px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-700"
                onClick={cancelEditing}
              >
                Cancel
              </button>
            )}
          </div>
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950/30 sm:flex-row sm:items-start sm:gap-3 sm:p-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 sm:gap-3">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
              <p className="text-sm text-green-900 dark:text-green-200">
                Selected trade:{" "}
                <span className="font-medium">{quickBooksTrade.tradeName}</span>
              </p>
            </div>
            <p className="mt-1 text-sm text-green-800 dark:text-green-300">
              All invoices imported from QuickBooks Online will be assigned to
              this trade.
            </p>
          </div>

          <div className="flex flex-shrink-0 justify-end">
            {isTradeEditable ? (
              <button
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/40"
                onClick={startEditing}
              >
                <Pencil className="h-3 w-3" />
                Change trade
              </button>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-help items-center gap-1 text-xs text-green-700 dark:text-green-400">
                      <Lock className="h-3 w-3" />
                      Trade is locked
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    The trade can only be changed before any data has been
                    imported.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      )}

      <ConfirmTradeSelectionModal
        isChange={!!quickBooksTrade}
        isOpen={isConfirmOpen}
        isSaving={isSaving}
        onClose={closeConfirm}
        onConfirm={confirmSave}
        tradeName={selectedTradeName}
      />
    </>
  );
};

export default QuickBooksTradeConfiguration;
